import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getItem, getContainer, updateItem, deleteItem } from '../services/api';
import { ArrowLeft, Box, Package, Barcode as BarcodeIcon, QrCode as QrCodeIcon, Trash2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [container, setContainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    barcode: '',
    quantity: 1
  });

  const fetchData = async () => {
    try {
      const itemRes = await getItem(id);
      setItem(itemRes.data);
      setFormData({
        name: itemRes.data.name,
        description: itemRes.data.description || '',
        barcode: itemRes.data.barcode || '',
        quantity: itemRes.data.quantity
      });

      if (itemRes.data.container_id) {
        const containerRes = await getContainer(itemRes.data.container_id);
        setContainer(containerRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateItem(id, {
        ...formData,
        container_id: item.container_id
      });
      setEditing(false);
      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      try {
        await deleteItem(id);
        // Navigate to container or items list
        if (container) {
          navigate(`/containers/${container.id}`);
        } else {
          navigate('/items');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item. Please try again.');
      }
    }
  };

  if (loading) return <div className="container">Loading...</div>;
  if (!item) return <div className="container">Item not found</div>;

  return (
    <div className="container">
      <button onClick={() => navigate(-1)} className="button button-secondary" style={{ marginBottom: '20px' }}>
        <ArrowLeft size={20} style={{ display: 'inline', marginRight: '5px' }} />
        Back
      </button>

      <div className="card">
        {!editing ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h1>
                  <Box size={28} style={{ display: 'inline', marginRight: '10px' }} />
                  {item.name}
                </h1>
                {item.description && (
                  <p style={{ color: '#666', marginTop: '10px' }}>{item.description}</p>
                )}
                
                <div style={{ marginTop: '20px' }}>
                  <p><strong>Quantity:</strong> {item.quantity}</p>
                  
                  {item.barcode && (
                    <p style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                      <BarcodeIcon size={20} style={{ marginRight: '8px' }} />
                      <strong>Barcode:</strong> <span style={{ marginLeft: '8px' }}>{item.barcode}</span>
                    </p>
                  )}
                  
                  {container && (
                    <p style={{ marginTop: '10px' }}>
                      <Package size={20} style={{ display: 'inline', marginRight: '8px' }} />
                      <strong>Container:</strong>{' '}
                      <span
                        style={{ color: '#007bff', cursor: 'pointer' }}
                        onClick={() => navigate(`/containers/${container.id}`)}
                      >
                        {container.name}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowQR(!showQR)} className="button button-secondary">
                  <QrCodeIcon size={20} />
                </button>
                <button onClick={() => setEditing(true)} className="button">
                  Edit
                </button>
                <button onClick={handleDelete} className="button button-danger">
                  <Trash2 size={20} />
                </button>
              </div>
            </div>

            {showQR && (
              <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <QRCodeSVG value={item.id} size={200} />
                <p style={{ marginTop: '10px', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
                  {item.id}
                </p>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2>Edit Item</h2>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className="input"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Barcode</label>
              <input
                type="text"
                className="input"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Quantity</label>
              <input
                type="number"
                className="input"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="button">Save</button>
              <button type="button" onClick={() => setEditing(false)} className="button button-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ItemDetail;
